/**
 * Says out loud that a listing is showing only part of what exists.
 *
 * Exists because the silent version of this cost real confusion: the pages screen showed 10 of 16
 * rows as though that were all of them, so creating a page that already existed failed with
 * "slug already exists" pointing at something invisible.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import type { JSX } from 'react';

const noticeStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  padding: var(--space-m);
  border: 1px solid var(--color-warning);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
  font-size: var(--font-size-2xs);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

export interface TruncatedListNoticeProps {
  shown: number;
  total: number;
  /** Plural noun for what is listed, e.g. "páginas". */
  noun: string;
  id?: string;
}

export const TruncatedListNotice = ({
  shown,
  total,
  noun,
  id,
}: TruncatedListNoticeProps): JSX.Element => (
  <div id={id} className={noticeStyle}>
    <GLIcon variant='Notifications.Warning' size={16} fill='var(--gl-yellow-dark)' />
    <span>
      Mostrando <strong>{shown}</strong> de <strong>{total}</strong> {noun}. O que não está aqui
      continua existindo — e um nome já usado por um item fora desta lista será recusado ao criar.
    </span>
  </div>
);
